<template>
    <div class="form-container card shadow-sm p-3">
      <div class="header d-flex justify-content-between align-items-center">
        <h4 class="form-title">
          {{ reposicao.id ? "Editar Aula de Reposição nº" : "Nova Aula de Reposição" }}
          <span v-if="reposicao.id">{{ reposicao.id }}</span>
        </h4>
        <button class="btn btn-secondary" @click="cancelar">Voltar</button>
      </div>
  
      <div class="spacer"></div>
  
      <!-- Seção 1: Dados do Aluno -->
      <div class="form-section">
        <h5 class="section-title">Dados do Aluno</h5>
        <div class="row">
          <!-- Campo Aluno -->
          <div class="col-md-6">
            <div class="form-group">
              <label for="aluno">Aluno <span class="required">*</span></label>
              <div class="d-flex">
                <input
                  id="aluno"
                  type="text"
                  class="form-control pessoa-input disabled-input"
                  :value="reposicao.aluno_nome || ''"
                  disabled
                />
                <button class="btn btn-primary pessoa-icon-btn" type="button" @click="abrirModalPessoa">
                  <i class="bi bi-person"></i>
                </button>
              </div>
            </div>
          </div>
          <!-- Campo Observações -->
          <div class="col-md-6">
            <div class="form-group">
              <label for="observacoes">Observações</label>
              <textarea id="observacoes" v-model="reposicao.observacoes" class="form-control" rows="3"></textarea>
            </div>
          </div>
        </div>
      </div>
  
      <!-- Seção 2: Dados da Requisição -->
      <div class="form-section">
        <h5 class="section-title">Dados da Requisição</h5>
        <div class="row">
          <!-- Campo Data da Requisição -->
          <div class="col-md-4">
            <div class="form-group">
              <label for="data_requisicao">Data <span class="required">*</span></label>
              <input
                id="data_requisicao"
                type="date"
                v-model="reposicao.data_requisicao"
                class="form-control"
                required
              />
            </div>
          </div>
          <!-- Campo Nível -->
          <div class="col-md-4">
            <div class="form-group">
              <label for="nivel">Nível <span class="required">*</span></label>
              <select
                id="nivel"
                v-model="reposicao.nivel_id"
                class="form-control"
                @change="onNivelChange"
                required
              >
                <option value="">Selecione</option>
                <option v-for="nivel in niveis" :key="nivel.id" :value="nivel.id">
                  {{ nivel.nivel }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <!-- Tabela de Turmas (múltiplas) -->
        <div class="form-group mt-3">
          <div class="table-responsive">
            <table class="table table-borderless">
              <thead>
                <tr>
                  <th>Turma <span class="required">*</span></th>
                  <th>Data da Aula <span class="required">*</span></th>
                  <th style="width:50px;"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(turma, index) in reposicao.turmas" :key="index">
                  <td>
                    <select
                      v-model="turma.turma_id"
                      class="form-control"
                      @change="handleTurmaChange(index, $event)"
                      required
                    >
                      <option value="" disabled>Selecione</option>
                      <option
                        v-for="op in turmasDisponiveis"
                        :key="`${op.id}|||${op.dia}|||${op.horario}`"
                        :value="`${op.id}|||${op.dia}|||${op.horario}`"
                      >
                        {{ `Turma: ${op.id} | Dia: ${op.dia} | Hora: ${String(op.horario||'').slice(0,5)} | Instrutor: ${op.instrutor}` }}
                      </option>
                    </select>
                  </td>
                  <td>
                    <flat-pickr
                      v-model="turma.data_aula"
                      :config="getDatePickerConfig(turma)"
                      class="form-control"
                      required
                    />
                  </td>
                  <td class="text-center">
                    <button @click="removerTurma(index)" class="btn btn-danger btn-sm">🗑️</button>
                  </td>
                </tr>
                <tr v-if="reposicao.turmas.length === 0">
                  <td colspan="3" class="text-center">Nenhuma turma cadastrada.</td>
                </tr>
              </tbody>
            </table>

            <div class="add-turma-container">
              <button class="btn btn-primary" @click="adicionarTurmaSlot">
                <i class="bi bi-plus"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Botões de Ação -->
      <div class="form-actions d-flex justify-content-end">
        <button
          class="btn me-2"
          :class="isSubmitting ? 'btn-success' : 'btn-primary'"
          :disabled="!formValid || isSubmitting"
          @click="salvarAulaReposicao"
        >
          <i :class="isSubmitting ? 'bi bi-hourglass-split me-1' : 'bi bi-save me-1'"></i>
          {{ isSubmitting ? 'Salvando…' : 'Salvar' }}
        </button>
        <button
          class="btn btn-secondary"
          :disabled="isSubmitting"
          @click="cancelar"
        >
          <i class="bi bi-x-circle me-1"></i>Cancelar
        </button>
      </div>

      <!-- Modal para seleção de pessoa -->
      <CarregarAlunos
        :mostrar="modalPessoaAberto"
        @fechar="fecharModalPessoa"
        @selecionar="selecionarPessoa"
      />
    </div>
  </template>
  
  <script>
  import { reactive, ref, onMounted, computed, getCurrentInstance } from "vue";
  import { useRouter, useRoute } from "vue-router";
  import axios from "@/services/axios";
  import flatPickr from "vue-flatpickr-component";
  import "flatpickr/dist/flatpickr.css";
  import { Portuguese } from "flatpickr/dist/l10n/pt.js";
  import CarregarAlunos from "@/components/modals/CarregarAlunos.vue";
  
  export default {
    name: "FormularioAulaReposicao",
    components: { CarregarAlunos, flatPickr },
    setup() {
      const router = useRouter();
      const route = useRoute();

      const isSubmitting = ref(false);
  
      // Nome do usuário logado (padrão do seu app)
      const { appContext } = getCurrentInstance();
      const userNameFromGlobal = appContext?.config?.globalProperties?.$globalState?.userName || null;

      // Dados da aula de reposição
      const reposicao = reactive({
        id: null,
        aluno_id: null,
        aluno_nome: "",
        observacoes: "",
        data_requisicao: "",
        nivel_id: "",
        turmas: [] // Cada item: { turma_id, data_aula, dia_semana, horario, turma_completa }
      });
  
      // Modal de pessoa (para seleção do Aluno)
      const modalPessoaAberto = ref(false);
      const abrirModalPessoa = () => {
        modalPessoaAberto.value = true;
      };
      const fecharModalPessoa = () => {
        modalPessoaAberto.value = false;
      };
      const selecionarPessoa = ({ matricula }) => {
        reposicao.aluno_nome = matricula.aluno_nome;
        reposicao.aluno_id = matricula.aluno_id;
        reposicao.matricula_id = matricula.id; // se precisar guardar o id da matrícula
        fecharModalPessoa();
      };
  
      // Carregar níveis via API
      const niveis = ref([]);
      const carregarNiveis = async () => {
        try {
          const response = await axios.get('/cadastros/niveis-turmas', {
            params: {
              page: 1,
              limit: 10000,    // ⚡ pega tudo (sem truncar nos 10)
              ativo: 'true',   // opcional: já filtra no back
              sort: 'nivel',   // se o endpoint aceitar
              order: 'asc',
            },
          });
          const dados = response.data?.data ?? response.data ?? [];
          niveis.value = Array.isArray(dados) ? dados.filter(n => n.ativo !== false) : [];
        } catch (error) {
          console.error('Erro ao carregar níveis:', error);
        }
      };
  
      // Carregar turmas disponíveis com base no nível selecionado
      const turmasDisponiveis = ref([]);

      const idxDia = (d) => {
        let s = String(d || '')
          .normalize('NFD').replace(/\p{Diacritic}/gu, '')
          .toLowerCase().replace(/-?feira/g, '').trim();
        s = s.split(/\s+/)[0].slice(0,3); // seg, ter, qua, qui, sex, sab, dom
        const mapa = { seg:1, ter:2, qua:3, qui:4, sex:5, sab:6, dom:7 };
        return mapa[s] ?? 99;
      };

      const toMin = (h) => {
        const m = String(h || '').match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
        if (!m) return Number.MAX_SAFE_INTEGER;
        return (+m[1]) * 60 + (+m[2]);
      };

      const carregarTurmas = async () => {
        if (!reposicao.nivel_id) return;

        // helper p/ normalizar nomes de nível (acentos/maiúsculas)
        const norm = s => String(s || '')
          .normalize('NFD').replace(/\p{Diacritic}/gu, '')
          .toLowerCase().trim();

        try {
          // ⚡ pega tudo (sem truncar em 10)
          const [responseDiasHorarios, responseTurmas] = await Promise.all([
            axios.get('/cadastros/dias-horarios-turmas', {
              params: { page: 1, limit: 10000, ativo: 'true' }
            }),
            axios.get('/cadastros/turmas', {
              params: { page: 1, limit: 10000, ativo: 'true' }
            })
          ]);

          const diasHorariosDados = responseDiasHorarios.data?.data ?? responseDiasHorarios.data ?? [];
          const turmasDados       = responseTurmas.data?.data ?? responseTurmas.data ?? [];

          // indexa turmas por ID p/ lookup O(1)
          const turmaById = new Map(
            (Array.isArray(turmasDados) ? turmasDados : []).map(t => [String(t.id), t])
          );

          const nivelSelecionado = niveis.value.find(n => n.id == reposicao.nivel_id);
          if (!nivelSelecionado) return;

          const alvo = norm(nivelSelecionado.nivel);

          const turmasProcessadas = (Array.isArray(diasHorariosDados) ? diasHorariosDados : [])
            .filter(t => norm(t.nivel_nome) === alvo)
            .map(t => {
              const tBase = turmaById.get(String(t.turma_id));
              // prioriza o que já veio da própria linha de dias/horários (se existir),
              // depois cai pro cadastro de turmas:
              const instrutorNome =
                t.instrutor_nome
                || tBase?.instrutor_nome
                || tBase?.instrutor?.nome
                || 'Não informado';

              return {
                id:       t.turma_id,
                dia:      t.dia_semana,
                horario:  t.horario,               // "HH:MM:SS"
                instrutor: instrutorNome,
                nivel:    t.nivel_nome
              };
            });

          // ordenação (mantém suas helpers idxDia / toMin)
          turmasDisponiveis.value = turmasProcessadas.sort((a, b) =>
            (Number(a.id) - Number(b.id)) ||
            (idxDia(a.dia) - idxDia(b.dia)) ||
            (toMin(a.horario) - toMin(b.horario))
          );

          // garante 1 slot
          if (!reposicao.turmas.length) {
            reposicao.turmas.push({ turma_id: '', data_aula: '' });
          }
        } catch (error) {
          console.error('Erro ao carregar turmas:', error);
        }
      };
  
      // Quando o nível for alterado
      const onNivelChange = async () => {
        await carregarTurmas();
      };
  
      // Adicionar uma nova linha para turma
      const adicionarTurmaSlot = () => {
        reposicao.turmas.push({ turma_id: "", data_aula: "" });
      };
  
      // Remover uma linha da tabela de turmas
      const removerTurma = (index) => {
        reposicao.turmas.splice(index, 1);
      };
  
      const handleTurmaChange = (index, event) => {
        const selectedValue = event.target.value; // Ex: "7|||domingo|||08:00:00"
        reposicao.turmas[index].turma_id = selectedValue;
        const parts = selectedValue.split("|||");
        if (parts.length >= 3) {
          reposicao.turmas[index].turmaId = parts[0];
          reposicao.turmas[index].dia_semana = parts[1];
          reposicao.turmas[index].horario = parts[2];
          const selectedTurma = turmasDisponiveis.value.find(t =>
            t.id.toString() === parts[0] &&
            t.dia.toLowerCase() === parts[1].toLowerCase() &&
            t.horario === parts[2]
          );
          const instrutor = selectedTurma ? selectedTurma.instrutor : "";
          reposicao.turmas[index].turma_completa = `Turma: ${parts[0]} | Dia: ${parts[1]} | Hora: ${parts[2]} | Instrutor: ${instrutor}`;
        }
      };
  
      const convertDayNameToNumber = (dayName) => {
        const dias = {
          "domingo": 0,
          "segunda-feira": 1,
          "terça-feira": 2,
          "quarta-feira": 3,
          "quinta-feira": 4,
          "sexta-feira": 5,
          "sábado": 6
        };
        return dias[dayName.toLowerCase()] ?? null;
      };
  
      const getDatePickerConfig = (turma) => {
        const configBase = {
          dateFormat: "Y-m-d",
          altInput: true,
          altFormat: "d/m/Y",
          locale: Portuguese
        };
  
        if (!turma.dia_semana) return configBase;
  
        const diaDesejado = convertDayNameToNumber(turma.dia_semana);
        return {
          ...configBase,
          disable: [
            function(date) {
              return date.getDay() !== diaDesejado;
            }
          ]
        };
      };
  
      // Validação do formulário (todos os campos obrigatórios, exceto observações)
      const formValid = computed(() => {
        if (!reposicao.aluno_id) return false;
        if (!reposicao.data_requisicao) return false;
        if (!reposicao.nivel_id) return false;
        if (!reposicao.turmas.length) return false;
        for (const turma of reposicao.turmas) {
          if (!turma.turma_id || !turma.data_aula) return false;
        }
        return true;
      });
  
      const getDiaSemanaPt = (isoDate) => {
        const d = new Date(`${isoDate}T00:00:00`);
        const map = ['domingo','segunda-feira','terça-feira','quarta-feira','quinta-feira','sexta-feira','sábado'];
        return map[d.getDay()];
      };

      const salvarAulaReposicao = async () => {
        if (isSubmitting.value) return;
        isSubmitting.value = true;

        // monta dados comuns (cabeçalho)
        const header = {
          aluno_id: reposicao.aluno_id,
          matricula_id: reposicao.matricula_id || null,
          data_requisicao: reposicao.data_requisicao,
          nivel_id: reposicao.nivel_id,
          observacoes: reposicao.observacoes || null
        };

        try {
          // ——— Modo EDIÇÃO: mantém comportamento atual (edita 1 registro) ———
          if (route.params.id) {
            const t = reposicao.turmas?.[0] || {};
            const [tid, diaSel, horaSel] = String(t.turma_id || '').split('|||');

            const payloadSingle = {
              ...header,
              data_aula: t.data_aula,
              turma_id: tid ? parseInt(tid, 10) : null,
              horario: t.horario || horaSel || null,
              dia_semana: t.dia_semana || diaSel || (t.data_aula ? getDiaSemanaPt(t.data_aula) : null),
              turma_completa: t.turma_completa || '',
              nome_usuario: userNameFromGlobal
            };

            const cfg = userNameFromGlobal ? { headers: { 'X-Usuario-Nome': userNameFromGlobal } } : {};
            await axios.put(`/operacional/aulas-reposicao/${route.params.id}`, payloadSingle, cfg);
            router.push('/operacional/aulas-reposicao');
            return;
          }

          // ——— Modo NOVO: cria N registros (um por linha) ———
          const linhasValidas = (reposicao.turmas || []).filter(l => l.turma_id && l.data_aula);
          if (!linhasValidas.length) {
            alert('Inclua pelo menos uma turma com data.');
            return;
          }

          const criados = []; // ids para rollback se falhar
          try {
            for (const l of linhasValidas) {
              const [tid, diaSel, horaSel] = String(l.turma_id || '').split('|||');

              // tenta reaproveitar valores já montados pelo handleTurmaChange
              const dia = l.dia_semana || diaSel || getDiaSemanaPt(l.data_aula);
              const hor = l.horario || horaSel || null;

              // monta turma_completa caso não tenha
              let turmaCompleta = l.turma_completa;
              if (!turmaCompleta) {
                const turmaDet = turmasDisponiveis.value.find(op =>
                  String(op.id) === String(tid) &&
                  String(op.dia).toLowerCase() === String(dia).toLowerCase() &&
                  String(op.horario) === String(hor)
                );
                const instrutor = turmaDet?.instrutor || '';
                turmaCompleta = `Turma: ${tid} | Dia: ${dia} | Hora: ${hor} | Instrutor: ${instrutor}`;
              }

              const payload = {
                ...header,
                data_aula: l.data_aula,
                turma_id: tid ? parseInt(tid, 10) : null,
                horario: hor,
                dia_semana: dia,
                turma_completa: turmaCompleta,
                nome_usuario: userNameFromGlobal
              };

              const cfg = userNameFromGlobal ? { headers: { 'X-Usuario-Nome': userNameFromGlobal } } : {};
              const { data } = await axios.post('/operacional/aulas-reposicao', payload, cfg);

              if (data?.id) criados.push(data.id);
            }

            router.push('/operacional/aulas-reposicao');
          } catch (err) {
            // rollback best-effort
            try {
              await Promise.all(criados.map(id => axios.delete(`/operacional/aulas-reposicao/${id}`)));
            } catch (e2) { /* ignora */ }
            throw err;
          }
        } catch (error) {
          console.error('Erro ao salvar aulas de reposição:', error);
          alert('Erro ao salvar aulas de reposição. Verifique os logs.');
        } finally {
          isSubmitting.value = false;
        }
      };
  
      const cancelar = () => {
        router.push("/operacional/aulas-reposicao");
      };
  

      function formatDateForInput(dateString) {
        if (!dateString) return "";
        // pega só 'YYYY-MM-DD' e evita shift de fuso
        return dateString.split("T")[0];
      }

      // Carregar dados para edição, se houver
      const carregarAulaReposicao = async (id) => {
        try {
          const response = await axios.get(`/operacional/aulas-reposicao/${id}`);
          const dados = response.data;

          // Preenche os campos principais
          reposicao.id              = dados.id;
          reposicao.aluno_id        = dados.aluno_id;
          reposicao.aluno_nome      = dados.aluno_nome;
          reposicao.matricula_id    = dados.matricula_id;
          reposicao.observacoes     = dados.observacoes;
          reposicao.data_requisicao = formatDateForInput(dados.data_requisicao);
          reposicao.nivel_id        = dados.nivel_id;        // <== esta linha é necessária

          // Mapeia a única turma no formato esperado pelo flatpickr sem shift de fuso
          reposicao.turmas = [{
            turma_id:       `${dados.turma_id}|||${dados.dia_semana}|||${dados.horario}`,
            data_aula:      formatDateForInput(dados.data_aula),
            horario:        dados.horario,
            dia_semana:     dados.dia_semana,
            turma_completa: dados.turma_completa
          }];

          // Recarrega o select de turmas (usa nivel_id para filtrar)
          await carregarTurmas();
        } catch (error) {
          console.error("Erro ao carregar aula de reposição:", error);
        }
      };
  
      onMounted(async () => {
        await carregarNiveis();
        // Garante que exista uma turma para cadastro
        if (!reposicao.turmas.length) {
            reposicao.turmas.push({ turma_id: "", data_aula: "" });
        }
        if (route.params.id) {
            await carregarAulaReposicao(route.params.id);
        } else {
            reposicao.data_requisicao = new Date().toISOString().split("T")[0];
            await carregarTurmas();
        }
      });
  
      return {
        reposicao,
        modalPessoaAberto,
        abrirModalPessoa,
        fecharModalPessoa,
        selecionarPessoa,
        niveis,
        turmasDisponiveis,
        onNivelChange,
        adicionarTurmaSlot,
        removerTurma,
        handleTurmaChange,
        getDatePickerConfig,
        salvarAulaReposicao,
        cancelar,
        formValid,
        isSubmitting,
      };
    }
  };
  </script>
  
  <style scoped>
  .flatpickr-calendar {
    font-family: 'Roboto', sans-serif;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }
  .flatpickr-day.selected {
    background-color: #1976d2;
    color: white;
  }
  .flatpickr-day:hover {
    background-color: #e0e0e0;
  }
  .form-container {
    margin: 20px;
  }
  .form-section {
    background: white;
    padding: 15px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
    margin-bottom: 1rem;
  }
  .section-title {
    font-size: 0.9rem;
    font-weight: bold;
    margin-bottom: 10px;
  }
  .disabled-input {
    background: #e9ecef;
    border: 1px solid #ced4da;
  }
  .required {
    color: red;
  }
  .pessoa-input {
    flex: 1;
  }
  .add-turma-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 5px;
  }
  .form-actions {
    margin-top: 20px;
  }
  .spacer {
    margin-bottom: 20px;
  }
  </style>
  